<template>
  <section id="contact" class="contact-section">
    <div class="section-header">
      <h2 class="section-title">{{ t('ContactSection.title') }}</h2>
    </div>

    <!-- LE FORMULAIRE -->
    <form 
      name="contact" 
      method="POST" 
      data-netlify="true" 
      class="tech-form"
      @submit.prevent="handleSubmit"
    >
      <input type="hidden" name="form-name" value="contact" />

      <div class="form-group">
        <label for="name">{{ t('ContactSection.label_user') }}</label>
        <input type="text" id="name" name="name" v-model="formData.name" required :placeholder="t('ContactSection.placeholder_user')" />
      </div>

      <div class="form-group">
        <label for="email">{{ t('ContactSection.label_email') }}</label>
        <input type="email" id="email" name="email" v-model="formData.email" required :placeholder="t('ContactSection.placeholder_email')" />
      </div>

      <div class="form-group">
        <label for="message">{{ t('ContactSection.label_message') }}</label>
        <textarea id="message" name="message" rows="5" v-model="formData.message" required :placeholder="t('ContactSection.placeholder_message')"></textarea>
      </div>

      <button type="submit" class="submit-btn" :disabled="isLoading">
        {{ isLoading ? t('ContactSection.btn_sending') : t('ContactSection.btn_send') }}
      </button>

      <p v-if="status" :class="['status-msg', status.type]">
        {{ status.text }}
      </p>
    </form>

    <!-- NOUVEAU : CONTACT DIRECT (EN DESSOUS) -->
    <div class="direct-contact">
      <h3>{{ t('ContactSection.direct_title') }}</h3>
      
      <a href="mailto:quentin.belhadj@protonmail.com" class="email-link">
        quentin.belhadj@protonmail.com
      </a>

      <div class="social-links">
        <a href="https://www.linkedin.com/in/quentin-belhadj-440711326/" target="_blank" class="social-icon">
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/linkedin/linkedin-original.svg" alt="LinkedIn" />
        </a>
        <a href="https://github.com/Quentin-BELHADJ" target="_blank" class="social-icon">
          <img src="https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/github/github-original.svg" alt="GitHub" class="github-icon" />
        </a>
      </div>
    </div>

  </section>
</template>

<script setup>
import { ref, reactive } from 'vue'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const formData = reactive({
  name: '',
  email: '',
  message: ''
})

const status = ref(null)
const isLoading = ref(false)

const handleSubmit = async () => {
  isLoading.value = true
  status.value = null

  const encode = (data) => {
    return Object.keys(data)
      .map(key => encodeURIComponent(key) + "=" + encodeURIComponent(data[key]))
      .join("&")
  }

  try {
    await fetch("/", { 
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({
        "form-name": "contact", 
        ...formData
      })
    })

    status.value = { type: 'success', text: t('ContactSection.success') }
    
    // Reset du formulaire
    formData.name = ''
    formData.email = ''
    formData.message = ''

  } catch (error) {
    status.value = { type: 'error', text: t('ContactSection.error') }
  } finally {
    isLoading.value = false
  }
}
</script>

<style scoped>
.contact-section {
  max-width: 600px;
  margin: 0 auto 100px;
  padding: 0 20px;
}

.section-header {
  text-align: center;
  margin-bottom: 40px;
}

.section-title {
  font-family: var(--font-tech, monospace);
  font-size: 2rem;
  text-transform: uppercase;
  letter-spacing: 2px;
  font-weight: 700;
}

.tech-form {
  background: #fafafa; 
  padding: 40px;
  border: 1px solid #333;
  border-radius: 4px;
  box-shadow: 0 10px 30px rgba(0,0,0,0.05);
}

.form-group {
  margin-bottom: 25px;
}

label {
  display: block;
  font-family: var(--font-tech, monospace);
  color: #666;
  margin-bottom: 8px;
  font-size: 0.8rem;
  letter-spacing: 1px;
}

input, textarea {
  width: 100%;
  background: transparent;
  border: none;
  border-bottom: 1px solid #333;
  color: var(--text-primary, #000);
  padding: 10px 0;
  font-family: var(--font-main, sans-serif);
  font-size: 1rem;
  transition: border-color 0.3s;
}

input:focus, textarea:focus {
  outline: none;
  border-bottom-color: #000;
  background: rgba(0,0,0,0.02);
}

.submit-btn {
  width: 100%;
  background: #000;
  color: #fff;
  border: none;
  padding: 15px;
  font-family: var(--font-tech, monospace);
  font-weight: bold;
  cursor: pointer;
  margin-top: 10px;
  transition: all 0.2s;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.submit-btn:hover {
  background: #333;
  letter-spacing: 2px;
}

.submit-btn:disabled {
  background: #666;
  cursor: not-allowed;
}

.status-msg {
  margin-top: 20px;
  font-family: monospace;
  font-weight: bold;
  text-align: center;
}
.status-msg.success { color: #00aa00; }
.status-msg.error { color: #ff0000; }

/* --- STYLE CONTACT DIRECT (EN DESSOUS) --- */
.direct-contact {
  margin-top: 60px;
  text-align: center;
  padding-top: 40px;
  border-top: 1px dashed #ccc;
}

.direct-contact h3 {
  font-family: var(--font-tech, monospace);
  font-size: 1rem;
  color: #666;
  margin-bottom: 15px;
}

.email-link {
  display: inline-block;
  font-size: 1.1rem;
  font-weight: bold;
  color: #000;
  text-decoration: none;
  margin-bottom: 30px;
  border-bottom: 2px solid transparent;
  transition: border-color 0.3s;
}

.email-link:hover {
  border-bottom-color: #000;
}

.social-links {
  display: flex;
  justify-content: center;
  gap: 30px;
}

.social-icon img {
  width: 40px;
  height: 40px;
  transition: transform 0.2s ease, filter 0.2s ease;
}

.social-icon:hover img {
  transform: scale(1.2);
  filter: grayscale(0%);
}
</style>